import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from './firebase'
import { pruneUndefined } from './firestore'
import { SHOW_TIMINGS } from './itineraryTypes'
import { combineDayAndTime, fromDateAndTimeStrings } from './zonedTime'
import type {

  ItineraryItem,
  Show,
  Tour,
  TourCrew,
  Venue,
  WithId,
} from '../types/models'

/* ============================================================================
   Writes.

   Everything here mirrors a write path in the Swift apps, because the Cloud
   Functions that send push and queue email trigger on these documents and read
   these exact field names. A field renamed on this side is a notification that
   silently never arrives.

   `undefined` is pruned rather than written: Firestore rejects it outright, and
   several Swift decoders treat an explicit null differently from an absent key.
   ============================================================================ */

/* ------------------------------------------------------------------- tours */

export interface TourDraft {
  tourName: string
  artist: string
  /** "2026-11-06" */
  startDate: string
  endDate: string
  posterURL?: string | null
  /** The artist this tour belongs to. Omitted only where the caller has no
   *  roster to choose from — `provisionAccount` adopts those on next load. */
  artistId?: string | null
}

export async function createTour(draft: TourDraft, ownerId: string): Promise<string> {
  const reference = await addDoc(
    collection(db, 'tours'),
    pruneUndefined({
      ownerId,
      tourName: draft.tourName.trim(),
      artist: draft.artist.trim(),
      startDate: Timestamp.fromDate(startOfLocalDay(draft.startDate)),
      endDate: Timestamp.fromDate(startOfLocalDay(draft.endDate)),
      posterURL: draft.posterURL?.trim() || undefined,
      artistId: draft.artistId || undefined,
      // The rules read membership from this map. The owner is implicit via
      // `ownerId`, but seeding it keeps the shape consistent for crew adds.
      members: {},
      createdAt: serverTimestamp(),
    }),
  )
  return reference.id
}

export async function updateTour(tourId: string, draft: TourDraft): Promise<void> {
  await updateDoc(
    doc(db, 'tours', tourId),
    pruneUndefined({
      tourName: draft.tourName.trim(),
      artist: draft.artist.trim(),
      startDate: Timestamp.fromDate(startOfLocalDay(draft.startDate)),
      endDate: Timestamp.fromDate(startOfLocalDay(draft.endDate)),
      posterURL: draft.posterURL?.trim() || null,
    }),
  )
}

/**
 * Deletes a tour and everything hanging off it.
 *
 * Firestore has no cascade. Leaving the children behind doesn't just waste
 * storage — orphaned `itineraryItems` still match the scheduled digest and
 * daily-briefing functions, so a deleted tour keeps sending push.
 */
export async function deleteTour(tourId: string): Promise<void> {
  const owned = [
    'shows',
    'itineraryItems',
    'flights',
    'hotels',
    'tourCrew',
    'setlists',
    'gearItems',
    'inputListItems',
    'productionDocuments',
    'supportActs',
  ]

  for (const path of owned) {
    const snapshot = await getDocs(query(collection(db, path), where('tourId', '==', tourId)))
    // Batches cap at 500 operations.
    for (const group of chunk(snapshot.docs, 400)) {
      const batch = writeBatch(db)
      group.forEach((entry) => batch.delete(entry.ref))
      await batch.commit()
    }
  }

  await deleteDoc(doc(db, 'tours', tourId))
}

/* ------------------------------------------------------------------- shows */

export interface ShowDraft {
  venueName: string
  address: string
  city: string
  country: string
  timezone: string
  /** "2026-11-06" */
  date: string
  /** Timing id → "20:00", or null when that point isn't in use. */
  timings: Partial<Record<string, string | null>>
  setDurationMinutes: number
  contactName: string
  contactEmail: string
  contactPhone: string
}

/**
 * Creates a show and its show-timing itinerary items in one batch.
 *
 * A show's running order is stored twice — as fields on the show, and as
 * `itineraryItems` flagged `isShowTiming` so they land on the day sheet. Both
 * writes go together or the itinerary shows times that no longer exist.
 */
export async function createShow(
  draft: ShowDraft,
  tourId: string,
  userId: string,
): Promise<string> {
  const batch = writeBatch(db)
  const showRef = doc(collection(db, 'shows'))

  batch.set(showRef, pruneUndefined(showFields(draft, tourId)))
  addTimingItems(batch, draft, showRef.id, tourId, userId)

  await batch.commit()
  return showRef.id
}

/**
 * Updates a show, rebuilding its timing items from scratch.
 *
 * Reconciling by type would leave stale rows behind whenever a timing is
 * switched off, so the existing set is deleted and rewritten — the same choice
 * `ShowWriter.update` makes.
 */
export async function updateShow(show: WithId<Show>, draft: ShowDraft): Promise<void> {
  // Scoped by tourId, then narrowed here.
  //
  // The rules resolve a tour lookup per document and refuse any tour-scoped
  // query that doesn't constrain `tourId`. Filtering on `showId` alone was
  // rejected, so this read threw before a single write went out and editing a
  // show failed with "Missing or insufficient permissions".
  const existing = (
    await getDocs(query(collection(db, 'itineraryItems'), where('tourId', '==', show.tourId)))
  ).docs.filter((entry) => {
    const data = entry.data() as ItineraryItem
    return data.showId === show.id && data.isShowTiming === true
  })

  const batch = writeBatch(db)
  existing.forEach((entry) => batch.delete(entry.ref))
  batch.set(doc(db, 'shows', show.id), pruneUndefined(showFields(draft, show.tourId)), {
    merge: true,
  })

  // Prefer the ownerId already on the show's items, then the tour's owner.
  // Falling back to the tour ID would write a tour ID into an ownerId field
  // and quietly break the security rules for every item.
  let ownerId = (existing[0]?.data() as ItineraryItem | undefined)?.ownerId
  if (!ownerId) {
    const tourDoc = await getDoc(doc(db, 'tours', show.tourId))
    ownerId = (tourDoc.data() as Tour | undefined)?.ownerId ?? ''
  }

  addTimingItems(batch, draft, show.id, show.tourId, ownerId)
  await batch.commit()
}

export async function deleteShow(show: WithId<Show>): Promise<void> {
  // Constrained by tourId for the same reason as `updateShow`.
  const items = (
    await getDocs(query(collection(db, 'itineraryItems'), where('tourId', '==', show.tourId)))
  ).docs.filter((entry) => (entry.data() as ItineraryItem).showId === show.id)
  const batch = writeBatch(db)
  items.forEach((entry) => batch.delete(entry.ref))
  batch.delete(doc(db, 'shows', show.id))
  await batch.commit()
}

function showFields(draft: ShowDraft, tourId: string) {
  const zone = draft.timezone
  const stamp = (timingId: string) => {
    const value = draft.timings[timingId]
    if (!value) return undefined
    const instant = fromDateAndTimeStrings(draft.date, value, zone)
    return instant ? Timestamp.fromDate(instant) : undefined
  }

  // The show's own date carries the headline set time when there is one, so a
  // show sorts by when it actually happens rather than by midnight.
  const headline = stamp('headline')
  const dayStart = fromDateAndTimeStrings(draft.date, '00:00', zone)

  return {
    tourId,
    date: headline ?? Timestamp.fromDate(dayStart ?? new Date(draft.date)),
    city: draft.city.trim(),
    country: draft.country.trim() || undefined,
    venueName: draft.venueName.trim(),
    venueAddress: draft.address.trim(),
    timezone: zone,
    contactName: draft.contactName.trim() || undefined,
    contactEmail: draft.contactEmail.trim() || undefined,
    contactPhone: draft.contactPhone.trim() || undefined,
    venueAccess: stamp('venueAccess') ?? null,
    loadIn: stamp('loadIn') ?? null,
    soundCheck: stamp('soundcheck') ?? null,
    doorsOpen: stamp('doors') ?? null,
    headlinerSetTime: headline ?? null,
    headlinerSetDurationMinutes: draft.setDurationMinutes,
    packOut: stamp('packOut') ?? null,
    createdAt: serverTimestamp(),
  }
}

function addTimingItems(
  batch: ReturnType<typeof writeBatch>,
  draft: ShowDraft,
  showId: string,
  tourId: string,
  userId: string,
) {
  for (const timing of SHOW_TIMINGS) {
    const value = draft.timings[timing.id]
    if (!value) continue
    const instant = fromDateAndTimeStrings(draft.date, value, draft.timezone)
    if (!instant) continue

    const ref = doc(collection(db, 'itineraryItems'))
    batch.set(
      ref,
      pruneUndefined({
        ownerId: userId,
        tourId,
        showId,
        title: timing.label,
        type: timing.id,
        timeUTC: Timestamp.fromDate(instant),
        timezone: draft.timezone,
        isShowTiming: true,
      }),
    )
  }
}

/* -------------------------------------------------------- itinerary items */

export interface ItineraryDraft {
  title: string
  type: string
  /** "2026-11-06" */
  date: string
  /** "15:00", or empty when the time isn't known yet. */
  time: string
  timezone: string
  subtitle?: string
  notes?: string
}

export async function createItineraryItem(
  draft: ItineraryDraft,
  tourId: string,
  userId: string,
): Promise<string> {
  const reference = await addDoc(
    collection(db, 'itineraryItems'),
    pruneUndefined(itineraryFields(draft, tourId, userId)),
  )
  return reference.id
}

export async function updateItineraryItem(
  itemId: string,
  draft: ItineraryDraft,
  tourId: string,
  userId: string,
): Promise<void> {
  await setDoc(
    doc(db, 'itineraryItems', itemId),
    pruneUndefined(itineraryFields(draft, tourId, userId)),
    { merge: true },
  )
}

export async function deleteItineraryItem(itemId: string): Promise<void> {
  await deleteDoc(doc(db, 'itineraryItems', itemId))
}

function itineraryFields(draft: ItineraryDraft, tourId: string, userId: string) {
  // No time yet: park the item at the start of its day so every sort, grouping
  // and export still has a concrete instant to work with, and flag it so the
  // row says "Set time" rather than showing a misleading midnight.
  const needsTime = draft.time.trim().length === 0
  const instant = fromDateAndTimeStrings(draft.date, needsTime ? '00:00' : draft.time, draft.timezone)

  return {
    ownerId: userId,
    tourId,
    title: draft.title.trim(),
    type: draft.type,
    timeUTC: Timestamp.fromDate(instant ?? new Date()),
    timezone: draft.timezone,
    subtitle: draft.subtitle?.trim() || undefined,
    notes: draft.notes?.trim() || undefined,
    needsTime: needsTime ? true : false,
  }
}

/* ----------------------------------------------------------------- flights */

export interface PassengerDraft {
  crewId: string
  /** Resolved from crew for display only — never written. */
  name: string
  baggage?: string
  seat?: string
}

export interface FlightDraft {
  airline: string
  flightNumber: string
  origin: string
  destination: string
  departureDate: string
  departureTime: string
  arrivalDate: string
  arrivalTime: string
  /** IANA zones for each end. A flight crosses zones; one won't do. */
  originTimezone: string
  destinationTimezone: string
  notes?: string
  bookingReference?: string
  passengers: PassengerDraft[]
}

/**
 * Creates or updates a flight.
 *
 * One function rather than a create/update pair, because the passenger list has
 * to be written wholesale either way — a merge would leave removed passengers
 * on the document, since Firestore merges maps but replaces arrays only when
 * the key is present.
 */
export async function saveFlight(
  draft: FlightDraft,
  tourId: string,
  ownerId: string,
  flightId?: string,
): Promise<string> {
  const fields = pruneUndefined(flightFields(draft, tourId, ownerId))

  if (flightId) {
    await setDoc(doc(db, 'flights', flightId), fields, { merge: true })
    return flightId
  }
  const reference = await addDoc(collection(db, 'flights'), fields)
  return reference.id
}

export async function deleteFlight(flightId: string): Promise<void> {
  await deleteDoc(doc(db, 'flights', flightId))
}

function flightFields(draft: FlightDraft, tourId: string, ownerId: string) {
  const departure = fromDateAndTimeStrings(
    draft.departureDate,
    draft.departureTime || '00:00',
    draft.originTimezone,
  )
  const arrival = fromDateAndTimeStrings(
    draft.arrivalDate,
    draft.arrivalTime || '00:00',
    draft.destinationTimezone,
  )

  return {
    tourId,
    ownerId,
    // The carrier code, not the airline's name — `AirlineLogo` and the native
    // `carrierCode` both parse this, and older records that stored a full name
    // are exactly why that parsing has three fallbacks.
    airline: draft.airline.trim().toUpperCase() || undefined,
    flightNumber: draft.flightNumber.trim().toUpperCase() || undefined,
    origin: draft.origin.trim().toUpperCase(),
    destination: draft.destination.trim().toUpperCase(),
    departureTimeUTC: Timestamp.fromDate(departure ?? new Date()),
    arrivalTimeUTC: Timestamp.fromDate(arrival ?? new Date()),
    notes: draft.notes?.trim() || undefined,
    bookingReference: draft.bookingReference?.trim().toUpperCase() || undefined,
    // `name` is display-only and deliberately dropped — the crew document is
    // the source of truth for it, and a stale copy here would outlive a rename.
    passengers: draft.passengers.map((passenger) =>
      pruneUndefined({
        crewId: passenger.crewId,
        baggage: passenger.baggage?.trim() || undefined,
        seat: passenger.seat?.trim().toUpperCase() || undefined,
      }),
    ),
  }
}

/* ------------------------------------------------------------------ hotels */

export interface HotelDraft {
  name: string
  address: string
  city: string
  country: string
  timezone: string
  checkInDate: string
  checkInTime: string
  checkOutDate: string
  checkOutTime: string
  bookingReference?: string
}

export async function createHotel(
  draft: HotelDraft,
  tourId: string,
  ownerId: string,
): Promise<string> {
  const reference = await addDoc(
    collection(db, 'hotels'),
    pruneUndefined({ ...hotelFields(draft, tourId, ownerId), rooms: [], createdAt: serverTimestamp() }),
  )
  return reference.id
}

export async function updateHotel(
  hotelId: string,
  draft: HotelDraft,
  tourId: string,
  ownerId: string,
): Promise<void> {
  await setDoc(doc(db, 'hotels', hotelId), pruneUndefined(hotelFields(draft, tourId, ownerId)), {
    merge: true,
  })
}

export async function deleteHotel(hotelId: string): Promise<void> {
  await deleteDoc(doc(db, 'hotels', hotelId))
}

function hotelFields(draft: HotelDraft, tourId: string, ownerId: string) {
  const checkIn = fromDateAndTimeStrings(
    draft.checkInDate,
    draft.checkInTime || '15:00',
    draft.timezone,
  )
  const checkOut = fromDateAndTimeStrings(
    draft.checkOutDate,
    draft.checkOutTime || '11:00',
    draft.timezone,
  )

  return {
    tourId,
    ownerId,
    name: draft.name.trim(),
    address: draft.address.trim(),
    city: draft.city.trim(),
    country: draft.country.trim(),
    // Non-optional in the Swift model — a hotel without it fails to decode.
    timezone: draft.timezone,
    checkInDate: Timestamp.fromDate(checkIn ?? new Date()),
    checkOutDate: Timestamp.fromDate(checkOut ?? new Date()),
    bookingReference: draft.bookingReference?.trim() || undefined,
  }
}

/* -------------------------------------------------------------------- crew */

export interface CrewDraft {
  name: string
  email: string
  phone: string
  roles: string[]
  accessLevel: 'editor' | 'viewer'
  /** Optional access window for temporary crew. */
  startDate?: string
  endDate?: string
}

/**
 * Adds someone to a tour.
 *
 * Two paths, matching the native apps:
 *
 * - **They already have an Encore account** — added to `tours.members` so the
 *   rules let them in, and given an in-app notification. No email, by design.
 * - **They don't** — a joining code goes in `invitations`, and a message is
 *   queued in `mail` for `processMailQueue` to send through Resend.
 *
 * The crew document is written first either way, so a failure in the notify
 * step leaves someone on the crew list who can be re-invited, rather than
 * losing them entirely.
 */
export async function addCrewMember(
  draft: CrewDraft,
  tour: WithId<Tour>,
  inviter: { id: string; name: string },
): Promise<{ crewId: string; joiningCode: string | null; existingUserId: string | null }> {
  const email = draft.email.trim().toLowerCase()
  const existingUserId = email ? await findUserIdByEmail(email) : null

  const joiningCode = existingUserId ? null : generateJoiningCode()

  const crewRef = await addDoc(
    collection(db, 'tourCrew'),
    pruneUndefined({
      tourId: tour.id,
      userId: existingUserId ?? undefined,
      contactId: undefined,
      name: draft.name.trim(),
      email: email || undefined,
      phone: draft.phone.trim() || undefined,
      roles: draft.roles,
      accessLevel: draft.accessLevel,
      status: existingUserId ? 'invited' : 'pending',
      invitationCode: joiningCode ?? undefined,
      startDate: draft.startDate
        ? Timestamp.fromDate(startOfLocalDay(draft.startDate))
        : undefined,
      endDate: draft.endDate ? Timestamp.fromDate(endOfLocalDay(draft.endDate)) : undefined,
      invitedBy: inviter.id,
      createdAt: serverTimestamp(),
    }),
  )

  if (existingUserId) {
    // Membership is what the rules read, and it is the ONLY thing they can
    // read — so the access window goes in here too, not just on the crew
    // document. A window the rules can't see is a window the SDK ignores.
    await setDoc(
      doc(db, 'tours', tour.id),
      { members: { [existingUserId]: membershipEntry(draft) } },
      { merge: true },
    )

    await addDoc(
      collection(db, 'notifications'),
      pruneUndefined({
        userId: existingUserId,
        recipientId: existingUserId,
        kind: 'tourInvitation',
        title: `${inviter.name} added you to ${tour.tourName}`,
        body: `${tour.artist} · ${draft.roles.join(', ')}`,
        tourId: tour.id,
        tourName: tour.tourName,
        artistName: tour.artist,
        inviterId: inviter.id,
        inviterName: inviter.name,
        crewDocId: crewRef.id,
        roles: draft.roles,
        hasBeenRead: false,
        createdAt: serverTimestamp(),
      }),
    )

    // An in-app notification alone is only seen by someone who opens the app.
    // Crew being added to a tour need telling wherever they are, so the email
    // goes out for existing accounts too — without a joining code, because
    // they already have somewhere to sign in.
    if (email) {
      await queueAddedEmail({
        to: email,
        recipientName: draft.name.trim(),
        tourName: tour.tourName,
        artist: tour.artist,
        inviterName: inviter.name,
        roles: draft.roles,
      })
    }
  } else if (joiningCode) {
    await setDoc(
      doc(db, 'invitations', joiningCode),
      pruneUndefined({
        tourId: tour.id,
        crewDocId: crewRef.id,
        inviterId: inviter.id,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 30 * 86_400_000)),
      }),
    )

    if (email) {
      await queueInvitationEmail({
        to: email,
        recipientName: draft.name.trim(),
        tourName: tour.tourName,
        artist: tour.artist,
        inviterName: inviter.name,
        roles: draft.roles,
        joiningCode,
      })
    }
  }

  return { crewId: crewRef.id, joiningCode, existingUserId }
}

/**
 * Adds someone to the crew by name alone.
 *
 * Used when a passenger is typed who isn't on the tour yet. A flight passenger
 * must resolve to a real `crewId` — that link is what puts the flight on their
 * own crew record — so the choice is either create the person or refuse the
 * name, and refusing the name is wrong when someone is mid-way through
 * building a manifest.
 *
 * They land as a pending viewer with no email, which is exactly what an
 * un-invited crew member is. The caller nudges the user to invite them
 * properly.
 */
export async function createCrewMemberByName(
  name: string,
  tourId: string,
  invitedBy: string,
  /** Links this crew record back to the organisation's person, so the roster
   *  can tell that two tours have booked the same human. */
  personId?: string,
): Promise<string> {
  const reference = await addDoc(
    collection(db, 'tourCrew'),
    pruneUndefined({
      tourId,
      name: name.trim(),
      personId: personId || undefined,
      roles: [],
      accessLevel: 'viewer' as const,
      status: 'pending' as const,
      invitedBy,
      createdAt: serverTimestamp(),
    }),
  )
  return reference.id
}

export async function updateCrewMember(crewId: string, draft: CrewDraft): Promise<void> {
  await setDoc(
    doc(db, 'tourCrew', crewId),
    pruneUndefined({
      name: draft.name.trim(),
      email: draft.email.trim().toLowerCase() || undefined,
      phone: draft.phone.trim() || undefined,
      roles: draft.roles,
      accessLevel: draft.accessLevel,
      startDate: draft.startDate
        ? Timestamp.fromDate(startOfLocalDay(draft.startDate))
        : undefined,
      endDate: draft.endDate ? Timestamp.fromDate(endOfLocalDay(draft.endDate)) : undefined,
    }),
    { merge: true },
  )
}

/**
 * The `tours.members` entry for a crew draft.
 *
 * `until` is pushed to the end of its day: the date input hands back midnight,
 * and someone whose last day is the 14th should keep access through the 14th
 * rather than losing it as the 13th ends. Mirrors `TourMembership.entry` in
 * Swift — if you change one, change the other.
 */
function membershipEntry(draft: CrewDraft) {
  return {
    level: draft.accessLevel,
    from: draft.startDate ? Timestamp.fromDate(startOfLocalDay(draft.startDate)) : null,
    until: draft.endDate ? Timestamp.fromDate(endOfLocalDay(draft.endDate)) : null,
  }
}

/** Removes someone from the tour, including the membership the rules read. */
export async function removeCrewMember(member: WithId<TourCrew>): Promise<void> {
  await deleteDoc(doc(db, 'tourCrew', member.id))

  if (member.userId) {
    const tourRef = doc(db, 'tours', member.tourId)
    const snapshot = await getDoc(tourRef)
    const members = { ...((snapshot.data() as Tour | undefined)?.members ?? {}) }
    delete members[member.userId]
    await setDoc(tourRef, { members }, { merge: true })
  }
}

async function findUserIdByEmail(email: string): Promise<string | null> {
  const snapshot = await getDocs(
    query(collection(db, 'users'), where('email', '==', email.toLowerCase())),
  )
  return snapshot.docs[0]?.id ?? null
}

/** Unambiguous alphabet — no O/0, I/1 — because these get read down a phone. */
function generateJoiningCode(length = 6): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = ''
  const values = new Uint32Array(length)
  crypto.getRandomValues(values)
  for (let index = 0; index < length; index += 1) {
    code += alphabet[values[index] % alphabet.length]
  }
  return code
}

/* ------------------------------------------------------------- guest list */

/**
 * The guest list hangs off the show, not the tour.
 *
 * `note` and `additionalGuests` are written as empty strings rather than
 * omitted, because that is what the Swift model does and its reader treats a
 * missing key and an empty string differently in one place. `additionalGuests`
 * is a string on purpose — it's a count typed into a text field.
 */
export async function addGuest(
  showId: string,
  guest: { name: string; note?: string; additionalGuests?: string },
): Promise<string> {
  const reference = await addDoc(collection(db, 'shows', showId, 'guestlist'), {
    name: guest.name.trim(),
    note: guest.note?.trim() ?? '',
    additionalGuests: guest.additionalGuests?.trim() ?? '',
  })
  return reference.id
}

export async function updateGuest(
  showId: string,
  guestId: string,
  guest: { name: string; note?: string; additionalGuests?: string },
): Promise<void> {
  await setDoc(
    doc(db, 'shows', showId, 'guestlist', guestId),
    {
      name: guest.name.trim(),
      note: guest.note?.trim() ?? '',
      additionalGuests: guest.additionalGuests?.trim() ?? '',
    },
    { merge: true },
  )
}

export async function deleteGuest(showId: string, guestId: string): Promise<void> {
  await deleteDoc(doc(db, 'shows', showId, 'guestlist', guestId))
}

/* -------------------------------------------------------------------- mail */

/**
 * Queues a message for `processMailQueue`.
 *
 * A successful write means queued, not delivered — the function writes a
 * `delivery` map back onto the same document with the real outcome.
 */
export async function queueMail(to: string[], subject: string, html: string, text: string) {
  await addDoc(collection(db, 'mail'), {
    to: to.map((address) => address.trim().toLowerCase()),
    message: { subject, html, text },
  })
}

/**
 * Sends the invitation again.
 *
 * Codes are reissued rather than reused. The old one stays valid until it
 * expires, which matters when someone forwards the first email and then asks
 * for another — both land on the same crew record, so whichever they type
 * works.
 *
 * Mirrors the two paths in `addCrewMember`: an existing account gets an in-app
 * notification and an email, a stranger gets a code and an email.
 */
export async function resendInvitation(
  member: WithId<TourCrew>,
  tour: WithId<Tour>,
  inviter: { id: string; name: string },
): Promise<{ joiningCode: string | null; existing: boolean }> {
  const email = (member.email ?? '').trim().toLowerCase()

  if (member.userId) {
    await addDoc(
      collection(db, 'notifications'),
      pruneUndefined({
        userId: member.userId,
        recipientId: member.userId,
        kind: 'tourInvitation',
        title: `${inviter.name} added you to ${tour.tourName}`,
        body: `${tour.artist}${member.roles?.length ? ` · ${member.roles.join(', ')}` : ''}`,
        tourId: tour.id,
        tourName: tour.tourName,
        artistName: tour.artist,
        inviterId: inviter.id,
        inviterName: inviter.name,
        crewDocId: member.id,
        roles: member.roles ?? [],
        hasBeenRead: false,
        createdAt: serverTimestamp(),
      }),
    )

    if (email) {
      await queueAddedEmail({
        to: email,
        recipientName: member.name,
        tourName: tour.tourName,
        artist: tour.artist,
        inviterName: inviter.name,
        roles: member.roles ?? [],
      })
    }

    return { joiningCode: null, existing: true }
  }

  const joiningCode = generateJoiningCode()

  await setDoc(
    doc(db, 'invitations', joiningCode),
    pruneUndefined({
      tourId: tour.id,
      crewDocId: member.id,
      inviterId: inviter.id,
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 30 * 86_400_000)),
    }),
  )

  await setDoc(
    doc(db, 'tourCrew', member.id),
    { invitationCode: joiningCode, status: 'pending' },
    { merge: true },
  )

  if (email) {
    await queueInvitationEmail({
      to: email,
      recipientName: member.name,
      tourName: tour.tourName,
      artist: tour.artist,
      inviterName: inviter.name,
      roles: member.roles ?? [],
      joiningCode,
    })
  }

  return { joiningCode, existing: false }
}

/** "You're on the tour" for somebody who already has an account — no code. */
async function queueAddedEmail(options: {
  to: string
  recipientName: string
  tourName: string
  artist: string
  inviterName: string
  roles: string[]
}) {
  const { to, recipientName, tourName, artist, inviterName, roles } = options
  const subject = `${inviterName} added you to ${tourName}`

  const text = [
    `Hi ${recipientName || 'there'},`,
    '',
    `${inviterName} added you to ${tourName} (${artist})${roles.length ? ` as ${roles.join(', ')}` : ''}.`,
    '',
    'It is already on your Encore account — open the app to see the schedule,',
    'travel and everything else you are on.',
    '',
    'https://encore-app.com/app',
  ].join('\n')

  const html = `<!doctype html>
<html><body style="margin:0;background:#0E0E11;font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:#ECECEC;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" width="100%" style="max-width:520px;background:#16161A;border:1px solid rgba(255,255,255,0.10);border-radius:14px;">
      <tr><td style="padding:28px;">
        <p style="margin:0 0 6px;font-size:11px;letter-spacing:1.4px;text-transform:uppercase;color:#FF7A2E;">You're on the tour</p>
        <h1 style="margin:0 0 4px;font-size:26px;line-height:1.15;color:#ECECEC;">${escapeHtml(tourName)}</h1>
        <p style="margin:0 0 22px;font-size:14px;color:rgba(236,236,236,0.62);">${escapeHtml(artist)}</p>
        <p style="margin:0 0 22px;font-size:15px;line-height:1.6;">
          ${escapeHtml(inviterName)} added you to this tour${roles.length ? ` as <strong>${escapeHtml(roles.join(', '))}</strong>` : ''}.
          It is already on your Encore account.
        </p>
        <a href="https://encore-app.com/app" style="display:inline-block;padding:12px 22px;border-radius:10px;background:#FF7A2E;color:#14100C;font-weight:700;text-decoration:none;">Open Encore</a>
      </td></tr>
    </table>
  </td></tr></table>
</body></html>`

  await queueMail([to], subject, html, text)
}

async function queueInvitationEmail(options: {
  to: string
  recipientName: string
  tourName: string
  artist: string
  inviterName: string
  roles: string[]
  joiningCode: string
}) {
  const { to, recipientName, tourName, artist, inviterName, roles, joiningCode } = options
  const subject = `${inviterName} added you to ${tourName}`

  const text = [
    `Hi ${recipientName || 'there'},`,
    '',
    `${inviterName} added you to ${tourName} (${artist})${roles.length ? ` as ${roles.join(', ')}` : ''}.`,
    '',
    `Your joining code is ${joiningCode}.`,
    '',
    'Create an account at https://encore-app.com and enter the code to see the schedule,',
    'travel and everything else you are on.',
  ].join('\n')

  const html = `<!doctype html>
<html><body style="margin:0;background:#0E0E11;font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:#ECECEC;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" width="100%" style="max-width:520px;background:#16161A;border:1px solid rgba(255,255,255,0.10);border-radius:14px;">
      <tr><td style="padding:28px;">
        <p style="margin:0 0 6px;font-size:11px;letter-spacing:1.4px;text-transform:uppercase;color:#FF7A2E;">You're on the tour</p>
        <h1 style="margin:0 0 4px;font-size:26px;line-height:1.15;color:#ECECEC;">${escapeHtml(tourName)}</h1>
        <p style="margin:0 0 22px;font-size:14px;color:rgba(236,236,236,0.62);">${escapeHtml(artist)}</p>
        <p style="margin:0 0 18px;font-size:15px;line-height:1.6;">
          ${escapeHtml(inviterName)} added you to this tour${roles.length ? ` as <strong>${escapeHtml(roles.join(', '))}</strong>` : ''}.
        </p>
        <p style="margin:0 0 8px;font-size:11px;letter-spacing:1.4px;text-transform:uppercase;color:rgba(236,236,236,0.38);">Joining code</p>
        <p style="margin:0 0 22px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:30px;letter-spacing:6px;color:#FF7A2E;">${escapeHtml(joiningCode)}</p>
        <a href="https://encore-app.com/app" style="display:inline-block;padding:12px 22px;border-radius:10px;background:#FF7A2E;color:#14100C;font-weight:700;text-decoration:none;">Open Encore</a>
        <p style="margin:22px 0 0;font-size:12px;color:rgba(236,236,236,0.38);">Create an account with this email address, then enter the code above.</p>
      </td></tr>
    </table>
  </td></tr></table>
</body></html>`

  await queueMail([to], subject, html, text)
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/* ----------------------------------------------------- venues & contacts */

export async function saveVenue(
  venue: Partial<Venue> & { name: string; city: string },
  ownerId: string,
  venueId?: string,
): Promise<string> {
  const fields = pruneUndefined({
    ownerId,
    name: venue.name.trim(),
    city: venue.city.trim(),
    address: venue.address?.trim() || undefined,
    country: venue.country?.trim() || undefined,
    capacity: venue.capacity ?? undefined,
    contactName: venue.contactName?.trim() || undefined,
    contactEmail: venue.contactEmail?.trim() || undefined,
    contactPhone: venue.contactPhone?.trim() || undefined,
  })

  if (venueId) {
    await setDoc(doc(db, 'venues', venueId), fields, { merge: true })
    return venueId
  }
  const reference = await addDoc(collection(db, 'venues'), {
    ...fields,
    createdAt: serverTimestamp(),
  })
  return reference.id
}

export async function deleteVenue(venueId: string): Promise<void> {
  await deleteDoc(doc(db, 'venues', venueId))
}

export interface ContactDraft {
  name: string
  roles: string[]
  email?: string
  phone?: string
  location?: string
  notes?: string
  countryOfBirth?: string
  allergies?: string
  medications?: string
  emergencyName?: string
  emergencyPhone?: string
  passportNumber?: string
  passportCountry?: string
  /** "2031-04-12" */
  passportIssued?: string
  passportExpiry?: string
  loyaltyPrograms?: Array<{ id: string; airline: string; accountNumber: string }>
}

/**
 * Saves a contact.
 *
 * `passport` and `emergencyContact` are nested objects in the Swift model, and
 * both are all-or-nothing: `PassportInfo` has no optional fields, so writing a
 * half-filled one makes the record fail to decode on the native side. They're
 * only written when they're complete.
 */
export async function saveContact(
  draft: ContactDraft,
  ownerId: string,
  contactId?: string,
): Promise<string> {
  const passportComplete =
    !!draft.passportNumber?.trim() &&
    !!draft.passportCountry?.trim() &&
    !!draft.passportIssued &&
    !!draft.passportExpiry

  const fields = pruneUndefined({
    ownerId,
    name: draft.name.trim(),
    roles: draft.roles ?? [],
    email: draft.email?.trim().toLowerCase() || undefined,
    phone: draft.phone?.trim() || undefined,
    location: draft.location?.trim() || undefined,
    notes: draft.notes?.trim() || undefined,
    countryOfBirth: draft.countryOfBirth?.trim() || undefined,
    allergies: draft.allergies?.trim() || undefined,
    medications: draft.medications?.trim() || undefined,
    emergencyContact:
      draft.emergencyName?.trim() && draft.emergencyPhone?.trim()
        ? { name: draft.emergencyName.trim(), phone: draft.emergencyPhone.trim() }
        : undefined,
    passport: passportComplete
      ? {
          passportNumber: draft.passportNumber!.trim(),
          issuingCountry: draft.passportCountry!.trim(),
          issuedDate: Timestamp.fromDate(startOfLocalDay(draft.passportIssued!)),
          expiryDate: Timestamp.fromDate(startOfLocalDay(draft.passportExpiry!)),
        }
      : undefined,
    loyaltyPrograms: draft.loyaltyPrograms?.length
      ? draft.loyaltyPrograms.filter((entry) => entry.airline.trim() || entry.accountNumber.trim())
      : undefined,
  })

  if (contactId) {
    await setDoc(doc(db, 'contacts', contactId), fields, { merge: true })
    return contactId
  }
  const reference = await addDoc(collection(db, 'contacts'), {
    ...fields,
    createdAt: serverTimestamp(),
  })
  return reference.id
}

export async function deleteContact(contactId: string): Promise<void> {
  await deleteDoc(doc(db, 'contacts', contactId))
}

/* ----------------------------------------------------------------- shared */

/** "2026-11-06" → local midnight. Tour dates are days, not instants. */
function startOfLocalDay(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return new Date(value)
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, 0, 0, 0)
}

function endOfLocalDay(value: string): Date {
  const start = startOfLocalDay(value)
  start.setHours(23, 59, 59, 999)
  return start
}

function chunk<T>(items: T[], size: number): T[][] {
  const groups: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    groups.push(items.slice(index, index + size))
  }
  return groups
}

export { combineDayAndTime }
